import React from 'react';
import { FormField, FormResponses, AddressValue } from '@/lib/forms/schema';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';

interface FormFieldRendererProps {
  field: FormField;
  value: string | boolean | number | AddressValue | null | undefined;
  onChange: (key: string, value: any) => void;
  error?: string;
  disabled?: boolean;
}

// Common countries for immigration forms
const COUNTRIES = [
  { value: 'BR', label: 'Brasil' },
  { value: 'US', label: 'Estados Unidos' },
  { value: 'MX', label: 'México' },
  { value: 'CO', label: 'Colômbia' },
  { value: 'AR', label: 'Argentina' },
  { value: 'VE', label: 'Venezuela' },
  { value: 'PE', label: 'Peru' },
  { value: 'CL', label: 'Chile' },
  { value: 'EC', label: 'Equador' },
  { value: 'GT', label: 'Guatemala' },
  { value: 'CU', label: 'Cuba' },
  { value: 'DO', label: 'República Dominicana' },
  { value: 'HN', label: 'Honduras' },
  { value: 'SV', label: 'El Salvador' },
  { value: 'NI', label: 'Nicarágua' },
  { value: 'CR', label: 'Costa Rica' },
  { value: 'PA', label: 'Panamá' },
  { value: 'PY', label: 'Paraguai' },
  { value: 'UY', label: 'Uruguai' },
  { value: 'BO', label: 'Bolívia' },
  { value: 'PT', label: 'Portugal' },
  { value: 'ES', label: 'Espanha' },
  { value: 'IT', label: 'Itália' },
  { value: 'FR', label: 'França' },
  { value: 'DE', label: 'Alemanha' },
  { value: 'UK', label: 'Reino Unido' },
  { value: 'CN', label: 'China' },
  { value: 'IN', label: 'Índia' },
  { value: 'PH', label: 'Filipinas' },
  { value: 'KR', label: 'Coreia do Sul' },
  { value: 'JP', label: 'Japão' },
  { value: 'OTHER', label: 'Outro' },
];

const US_STATES = [
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' },
  { value: 'DC', label: 'District of Columbia' },
];

export function FormFieldRenderer({
  field,
  value,
  onChange,
  error,
  disabled = false,
}: FormFieldRendererProps) {
  const handleChange = (newValue: any) => {
    onChange(field.key, newValue);
  };

  const baseInputClass = cn(
    'w-full',
    error && 'border-destructive focus-visible:ring-destructive'
  );

  const renderField = () => {
    switch (field.type) {
      case 'text':
      case 'email':
      case 'phone':
      case 'ssn':
        return (
          <Input
            type={field.type === 'email' ? 'email' : 'text'}
            value={(value as string) || ''}
            onChange={(e) => handleChange(e.target.value)}
            placeholder={field.placeholder}
            disabled={disabled}
            className={baseInputClass}
          />
        );

      case 'number':
      case 'currency':
        return (
          <Input
            type="number"
            value={(value as number) || ''}
            onChange={(e) => handleChange(e.target.value ? Number(e.target.value) : null)}
            placeholder={field.placeholder}
            disabled={disabled}
            className={baseInputClass}
          />
        );

      case 'textarea':
        return (
          <Textarea
            value={(value as string) || ''}
            onChange={(e) => handleChange(e.target.value)}
            placeholder={field.placeholder}
            disabled={disabled}
            className={cn(baseInputClass, 'min-h-[100px]')}
          />
        );

      case 'date':
        return (
          <Input
            type="date"
            value={(value as string) || ''}
            onChange={(e) => handleChange(e.target.value)}
            disabled={disabled}
            className={baseInputClass}
          />
        );

      case 'select':
      case 'country':
        const options = field.type === 'country' ? COUNTRIES : field.options || [];
        return (
          <Select
            value={(value as string) || ''}
            onValueChange={handleChange}
            disabled={disabled}
          >
            <SelectTrigger className={baseInputClass}>
              <SelectValue placeholder={field.placeholder || 'Selecione...'} />
            </SelectTrigger>
            <SelectContent>
              {options.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );

      case 'radio':
        return (
          <RadioGroup
            value={(value as string) || ''}
            onValueChange={handleChange}
            disabled={disabled}
            className="flex flex-col space-y-2"
          >
            {field.options?.map((option) => (
              <div key={option.value} className="flex items-center space-x-2">
                <RadioGroupItem value={option.value} id={`${field.key}-${option.value}`} />
                <Label 
                  htmlFor={`${field.key}-${option.value}`}
                  className="font-normal cursor-pointer"
                >
                  {option.label}
                </Label>
              </div>
            ))}
          </RadioGroup>
        );

      case 'checkbox':
        return (
          <div className="flex items-center space-x-2">
            <Checkbox
              checked={(value as boolean) || false}
              onCheckedChange={handleChange}
              disabled={disabled}
              id={field.key}
            />
            <Label htmlFor={field.key} className="font-normal cursor-pointer">
              {field.options?.[0]?.label || 'Sim'}
            </Label>
          </div>
        );

      case 'address':
        const addressValue = (value as AddressValue) || {};
        const addressFields = field.addressFields || ['street', 'city', 'state', 'zip', 'country'];
        
        return (
          <div className="space-y-3">
            {addressFields.includes('street') && (
              <Input
                placeholder="Endereço (rua, número, complemento)"
                value={addressValue.street || ''}
                onChange={(e) => handleChange({ ...addressValue, street: e.target.value })}
                disabled={disabled}
                className={baseInputClass}
              />
            )}
            <div className="grid grid-cols-2 gap-3">
              {addressFields.includes('city') && (
                <Input
                  placeholder="Cidade"
                  value={addressValue.city || ''}
                  onChange={(e) => handleChange({ ...addressValue, city: e.target.value })}
                  disabled={disabled}
                  className={baseInputClass}
                />
              )}
              {addressFields.includes('state') && (
                <Select
                  value={addressValue.state || ''}
                  onValueChange={(v) => handleChange({ ...addressValue, state: v })}
                  disabled={disabled}
                >
                  <SelectTrigger className={baseInputClass}>
                    <SelectValue placeholder="Estado" />
                  </SelectTrigger>
                  <SelectContent>
                    {US_STATES.map((state) => (
                      <SelectItem key={state.value} value={state.value}>
                        {state.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              {addressFields.includes('zip') && (
                <Input
                  placeholder="CEP / ZIP Code"
                  value={addressValue.zip || ''}
                  onChange={(e) => handleChange({ ...addressValue, zip: e.target.value })}
                  disabled={disabled}
                  className={baseInputClass}
                />
              )}
              {addressFields.includes('country') && (
                <Select
                  value={addressValue.country || ''}
                  onValueChange={(v) => handleChange({ ...addressValue, country: v })}
                  disabled={disabled}
                >
                  <SelectTrigger className={baseInputClass}>
                    <SelectValue placeholder="País" />
                  </SelectTrigger>
                  <SelectContent>
                    {COUNTRIES.map((country) => (
                      <SelectItem key={country.value} value={country.value}>
                        {country.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
        );

      case 'file':
        return (
          <Input
            type="file"
            onChange={(e) => handleChange(e.target.files?.[0]?.name || null)}
            disabled={disabled}
            className={baseInputClass}
          />
        );

      case 'signature':
        return (
          <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-8 text-center">
            <p className="text-muted-foreground text-sm">
              Assinatura digital (em desenvolvimento)
            </p>
          </div>
        );

      default:
        return (
          <Input
            value={(value as string) || ''}
            onChange={(e) => handleChange(e.target.value)}
            placeholder={field.placeholder}
            disabled={disabled}
            className={baseInputClass}
          />
        );
    }
  };

  return (
    <div className={cn('space-y-2', field.fullWidth && 'col-span-full')}>
      <Label className={cn('text-sm font-medium', field.required && "after:content-['*'] after:ml-0.5 after:text-destructive")}>
        {field.label}
      </Label>
      {renderField()}
      {field.helperText && !error && (
        <p className="text-xs text-muted-foreground">{field.helperText}</p>
      )}
      {error && (
        <p className="text-xs text-destructive">{error}</p>
      )}
    </div>
  );
}
